/**
 * API Tokens Page Component
 *
 * Main page for managing API tokens.
 * Combines list, create modal, and token display modal.
 */
import React, { useState } from 'react';
import {
  Container,
  Stack,
  Group,
  Button,
  Text,
  Alert,
  Badge,
  Paper,
  Title
} from '@mantine/core';
import {
  IconKey,
  IconPlus,
  IconAlertTriangle,
  IconRefresh
} from '@tabler/icons-react';
import tokens from '../../theme/designTokens';
import APITokensList from './APITokens/APITokensList';
import CreateTokenModal from './APITokens/CreateTokenModal';
import TokenCreatedModal from './APITokens/TokenCreatedModal';

/**
 * APITokensPage Component
 *
 * Full page for API token management
 */
const APITokensPage = () => {
  const [createModalOpened, setCreateModalOpened] = useState(false);
  const [tokenCreatedModalOpened, setTokenCreatedModalOpened] = useState(false);
  const [createdTokenData, setCreatedTokenData] = useState(null);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Handle successful token creation
  const handleTokenCreated = (tokenData) => {
    setCreatedTokenData(tokenData);
    setTokenCreatedModalOpened(true);
    // Trigger list refresh
    setRefreshTrigger(prev => prev + 1);
  };

  // Handle refresh button
  const handleRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="xl">
        {/* Page header */}
        <Group justify="space-between" align="flex-start">
          <Stack gap="xs">
            <Group gap="sm">
              <IconKey size={32} color={tokens.colors.brand.primary} />
              <Title order={1} c={tokens.colors.neutral[8]}>
                API Токены
              </Title>
            </Group>
            <Text size="sm" c={tokens.colors.neutral[6]}>
              Управление токенами для аутентификации API запросов
            </Text>
          </Stack>

          <Group gap="sm">
            <Button
              variant="subtle"
              color="gray"
              leftSection={<IconRefresh size={18} />}
              onClick={handleRefresh}
            >
              Обновить
            </Button>
            <Button
              variant="filled"
              color="portal-blue"
              leftSection={<IconPlus size={18} />}
              onClick={() => setCreateModalOpened(true)}
            >
              Создать токен
            </Button>
          </Group>
        </Group>

        {/* Important info alert */}
        <Alert
          icon={<IconAlertTriangle size={16} />}
          title="Важная информация"
          color="blue"
          variant="light"
        >
          <Stack gap="xs">
            <Text size="sm">
              API токены используются для аутентификации запросов к CRM API.
              Храните токены в безопасности и никогда не передавайте их третьим лицам.
            </Text>
            <Group gap="xs">
              <Badge color="blue" variant="light" size="sm">
                Формат токена
              </Badge>
              <Text size="xs" c={tokens.colors.neutral[6]} style={{ fontFamily: 'monospace' }}>
                crm_&lt;40_символов&gt;
              </Text>
            </Group>
          </Stack>
        </Alert>

        {/* Security best practices */}
        <Paper
          p="md"
          style={{
            backgroundColor: tokens.colors.neutral[0],
            border: `1px solid ${tokens.colors.neutral[3]}`
          }}
        >
          <Text size="sm" fw={600} c={tokens.colors.neutral[8]} mb="xs">
            Рекомендации по безопасности:
          </Text>
          <Stack gap={6}>
            <Group gap="xs">
              <Text size="sm" c={tokens.colors.neutral[7]}>
                • Используйте отдельные токены для разных сред (production, development)
              </Text>
            </Group>
            <Group gap="xs">
              <Text size="sm" c={tokens.colors.neutral[7]}>
                • Устанавливайте минимальные необходимые права доступа (scopes)
              </Text>
            </Group>
            <Group gap="xs">
              <Text size="sm" c={tokens.colors.neutral[7]}>
                • Регулярно обновляйте токены и деактивируйте неиспользуемые
              </Text>
            </Group>
            <Group gap="xs">
              <Text size="sm" c={tokens.colors.neutral[7]}>
                • Используйте срок действия для повышения безопасности
              </Text>
            </Group>
          </Stack>
        </Paper>

        {/* Tokens list */}
        <Stack gap="md">
          <Group justify="space-between">
            <Text size="lg" fw={600} c={tokens.colors.neutral[8]}>
              Активные токены
            </Text>
          </Group>

          <APITokensList
            onRefresh={refreshTrigger}
          />
        </Stack>

        {/* API Documentation link */}
        <Paper
          p="md"
          style={{
            backgroundColor: tokens.colors.brand.primary + '08',
            border: `1px solid ${tokens.colors.brand.primary}`
          }}
        >
          <Stack gap="xs">
            <Text size="sm" fw={600} c={tokens.colors.neutral[8]}>
              Как использовать API токены?
            </Text>
            <Text size="sm" c={tokens.colors.neutral[7]}>
              Добавьте токен в HTTP заголовок каждого запроса к API:
            </Text>
            <Paper
              p="sm"
              style={{
                backgroundColor: tokens.colors.neutral[0],
                fontFamily: 'Consolas, Monaco, monospace',
                fontSize: '12px'
              }}
            >
              <Text c={tokens.colors.neutral[7]}>
                X-API-Key: crm_your_token_here
              </Text>
            </Paper>
            <Text size="xs" c={tokens.colors.neutral[6]}>
              Документация API: <a href="/api/docs" style={{ color: tokens.colors.brand.primary }}>
                /api/docs
              </a>
            </Text>
          </Stack>
        </Paper>
      </Stack>

      {/* Create Token Modal */}
      <CreateTokenModal
        opened={createModalOpened}
        onClose={() => setCreateModalOpened(false)}
        onSuccess={handleTokenCreated}
      />

      {/* Token Created Modal */}
      <TokenCreatedModal
        opened={tokenCreatedModalOpened}
        onClose={() => {
          setTokenCreatedModalOpened(false);
          setCreatedTokenData(null);
        }}
        tokenData={createdTokenData}
      />
    </Container>
  );
};

export default APITokensPage;
